import React from 'react';
import {
  Box,
  Container,
  Flex,
  Button,
  HStack,
  VStack,
  IconButton,
  useColorModeValue,
  useDisclosure,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '@chakra-ui/react';
import Logo from './Logo';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';

const MobileNav = ({ isOpen, onClose, handleSignIn, user, navigate, handleSignOut, isAdmin }) => {
  const bg = useColorModeValue('white', 'gray.800');
  
  return (
    <Box
      display={{ base: isOpen ? 'block' : 'none', md: 'none' }}
      position="absolute"
      top="60px"
      left={0}
      right={0}
      bg={bg}
      p={4}
      borderBottom="1px"
      borderColor="gray.200"
      zIndex={999}
      boxShadow="md"
    >
      <VStack spacing={4} align="stretch">
        <Button
          as="a"
          href="/pricing"
          variant="ghost"
          fontSize="sm"
          fontWeight="medium"
          color="gray.600"
          _hover={{ color: 'blue.500' }}
          onClick={onClose}
        >
          Pricing
        </Button>
        <Button
          as="a"
          href="/faq"
          variant="ghost"
          fontSize="sm"
          fontWeight="medium"
          color="gray.600"
          _hover={{ color: 'blue.500' }}
          onClick={onClose}
        >
          FAQ
        </Button>
        {user ? (
          <>
            <Button
              w="full"
              size="sm"
              colorScheme="blue"
              onClick={() => {
                navigate('/dashboard');
                onClose();
              }}
            >
              Dashboard
            </Button>
            {isAdmin && (
              <Button
                w="full"
                size="sm"
                colorScheme="purple"
                onClick={() => {
                  navigate('/admin');
                  onClose();
                }}
              >
                Admin Panel
              </Button>
            )}
            <Button
              w="full"
              size="sm"
              variant="ghost"
              onClick={() => {
                handleSignOut();
                onClose();
              }}
            >
              Sign Out
            </Button>
          </>
        ) : (
          <Button
            w="full"
            size="sm"
            colorScheme="blue"
            leftIcon={<FcGoogle />}
            onClick={() => {
              handleSignIn();
              onClose();
            }}
            variant="outline"
          >
            Start Free
          </Button>
        )}
      </VStack>
    </Box>
  );
};

export const Header = () => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { signIn, signOut, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onToggle, onClose } = useDisclosure();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      toast({
        title: 'Error signing out',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSignIn = async () => {
    try {
      await signIn();
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error signing in',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bg={bg}
      borderBottom="1px"
      borderColor={borderColor}
      zIndex={1000}
    >
      <Container maxW="7xl">
        <Flex py={4} align="center" justify="space-between">
          <Box 
            as="a" 
            href="/" 
            _hover={{ opacity: 0.8 }}
            width={{ base: "150px", sm: "180px", md: "220px" }}
            height={{ base: "35px", md: "40px" }}
            display="flex"
            alignItems="center"
            cursor="pointer"
          >
            <Logo variant="full" size="lg" />
          </Box>

          {/* Desktop Navigation */}
          <HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
            <Button
              as="a"
              href="/pricing"
              variant="ghost"
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
              _hover={{ color: 'blue.500' }}
            >
              Pricing
            </Button>
            <Button
              as="a"
              href="/faq"
              variant="ghost"
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
              _hover={{ color: 'blue.500' }}
            >
              FAQ
            </Button>
            {user ? (
              <HStack spacing={4}>
                {isAdmin && (
                  <Button
                    size="sm"
                    colorScheme="purple"
                    onClick={() => navigate('/admin')}
                  >
                    Admin Panel
                  </Button>
                )}
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard
                </Button>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    size="sm"
                    variant="ghost"
                  >
                    {user.displayName || 'User'}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            ) : (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FcGoogle />}
                onClick={handleSignIn}
                variant="outline"
              >
                {/* Responsive button text */}
                <Box display={{ base: 'none', sm: 'block' }}>Start with 3 Free Emails/Day</Box>
                <Box display={{ base: 'block', sm: 'none' }}>Start Free</Box>
              </Button>
            )}
          </HStack>

          {/* Mobile Navigation Button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant="ghost"
            aria-label="Toggle Navigation"
            size="sm"
          />
        </Flex>
      </Container>
      
      {/* Mobile Navigation Menu */}
      <MobileNav
        isOpen={isOpen}
        onClose={onClose}
        handleSignIn={handleSignIn}
        user={user}
        navigate={navigate}
      />
    </Box>
  );
};
